import { useExperiments } from '@alltrails/experiments';
import { useClientNavigationSearchParams } from '@alltrails/core/hooks/useClientNavigation';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';

/**
 * Hook for iOS-specific web checkout optimization experiment logic
 *
 * Experiment: web-growth-rm-mobile-to-web-checkout-optimization
 *
 * Detects iOS native app webview via:
 * - app_source=iOS query parameter
 *
 * Replaces the pattern:
 * `experiments['web-growth-rm-mobile-to-web-checkout-optimization']?.value !== 'control' && isIos`
 *
 * With explicit treatment variant checks and webview detection.
 */
export default function useIosWebCheckoutExperiment() {
  const experiments = useExperiments();
  const searchParams = useClientNavigationSearchParams();

  const experimentValue = experiments['web-growth-rm-mobile-to-web-checkout-optimization']?.value;
  // Individual variant flags
  const isHierarchyUpdatesVariant = experimentValue === 'hierarchy_updates';
  const isHierarchyAndCardCtaVariant = experimentValue === 'hierarchy_and_card_cta';
  const isApplePayOnlyVariant = experimentValue === 'apple_pay_only';
  const isHierarchyCardCtaAndTimelineVariant = experimentValue === 'hierarchy_card_cta_and_timeline';
  const isAppStoreStyleVariant = experimentValue === 'app_store_style';

  // Check if user is in a treatment variant (not control)
  const isTreatmentVariant =
    isHierarchyUpdatesVariant ||
    isHierarchyAndCardCtaVariant ||
    isApplePayOnlyVariant ||
    isHierarchyCardCtaAndTimelineVariant ||
    isAppStoreStyleVariant;

  // Detect iOS native app webview
  const appSource = searchParams.get('app_source');
  const isIosNativeApp = appSource === 'iOS';
  const isMobile = useIsMobileSizedScreen();

  // The iOS checkout optimization logic - only enabled for native app webviews
  const isIosWebCheckoutOptimizationEnabled = isMobile && isIosNativeApp && isTreatmentVariant;

  return {
    isIosWebCheckoutOptimizationEnabled,
    isHierarchyUpdatesVariant,
    isHierarchyAndCardCtaVariant,
    isApplePayOnlyVariant,
    isHierarchyCardCtaAndTimelineVariant,
    isAppStoreStyleVariant,
    isIosNativeApp
  };
}
